import { PUBLIC_TRUSTARC_DOMAIN, PUBLIC_TRUSTARC_URL } from '$env/static/public';
import { pushEvent } from '../gtm/index';

import {
    ALLOWED_CONSENT_TYPES_NAMES,
    CONSENT_DECISION_NAME,
    CONSENT_GDPR_DECISION_NAME,
    CONSENT_IMPLIED_PARAMS,
    CONSENT_PARAMS,
    ISO_639_2_LANGS,
    MARKETS
} from './constants';
import type { TPrivacyManagerApiResponse } from './types';

export const hasDecision = (data: number[] | null) =>
    Boolean(data && Array.isArray(data) && data.length > 0 && !data.includes(0));

export const getGTMEventName = (type: number) => {
    const typeName = ALLOWED_CONSENT_TYPES_NAMES[type] || ALLOWED_CONSENT_TYPES_NAMES[0];

    return `Cookies Consent ${typeName}`;
};

const handleAPIResponse = (
    response: TPrivacyManagerApiResponse,
    onScriptLoadedCallback: () => void
) => {
    if (!response.capabilities || response.action !== CONSENT_DECISION_NAME) {
        return;
    }

    const newDecision = window.PrivacyManagerAPI?.callApi(
        CONSENT_GDPR_DECISION_NAME,
        PUBLIC_TRUSTARC_DOMAIN
    ).consentDecision;

    if (hasDecision(newDecision)) {
        // push changes only once when cookie was not set
        newDecision.forEach((label: number) => {
            pushEvent(getGTMEventName(label));
        });
    }
    onScriptLoadedCallback();
};

export const onPrivacyManagerApiLoaded = (onScriptLoadedCallback: () => void) => {
    const onMessagehandler = (e: MessageEvent) => {
        try {
            const json = JSON.parse(e.data);

            if (json.PrivacyManagerAPI) {
                handleAPIResponse(
                    json.PrivacyManagerAPI as TPrivacyManagerApiResponse,
                    onScriptLoadedCallback
                );
            }
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (e: any) {
            if (e.name !== 'SyntaxError') {
                window.console.error(`Unable to handle trustarc message ${e.message}`, e);
            }
        }
    };
    window.addEventListener('message', onMessagehandler, false);

    const apiObject = {
        PrivacyManagerAPI: {
            self: PUBLIC_TRUSTARC_DOMAIN,
            action: CONSENT_DECISION_NAME,
            timestamp: new Date().getTime()
        }
    };
    window.top?.postMessage(JSON.stringify(apiObject), '*');

    return () => {
        window.removeEventListener('message', onMessagehandler, false);
    };
};

export const getScriptUrl = (
    implied: boolean,
    lang: string,
    privacypolicylink: string,
    retryAttempt: number
) => {
    const url = new URL(PUBLIC_TRUSTARC_URL);
    const queryParams = {
        ...CONSENT_PARAMS,
        ...(implied ? CONSENT_IMPLIED_PARAMS : {}),
        retryAttempt,
        language: ISO_639_2_LANGS[lang] || ISO_639_2_LANGS[MARKETS.EN],
        ...(MARKETS.ENGB === lang && { country: 'GB' }),
        // Use this parameter to override the privacy policy link in the CM.
        // This url must be encoded and MUST BE placed as the last parameter in the CM script
        ...(privacypolicylink && {
            cookieLink: `/${lang}${privacypolicylink}`
        })
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } as Record<string, any>;

    Object.keys(queryParams).forEach((key) => {
        url.searchParams.append(key, queryParams[key]);
    });

    return url.toString();
};
